import {useEffect, useRef, useState} from 'react';
import {
   GetPayload,
   PromiseListener,
   PromiseListenerAsyncFunction,
   PromiseListenerAsyncFunctionConfig,
   SetPayload
} from './PromiseListener';

export type CreateAsyncFunctionType = (...args: Array<any>) => Promise<any>;

export interface MakeAsyncFunctionProps<T,U=T,V=any,W=any,E=any> {
   listener: PromiseListener;
   start: string;
   resolve: string;
   reject: string;
   setPayload?: SetPayload<T,U>;
   getPayload?: GetPayload<V,W>;
   getError?: GetPayload<E>;
   children: (arg0: CreateAsyncFunctionType) => JSX.Element;
}

const createAsyncFunction = <T,U=T>(props: MakeAsyncFunctionProps<T,U>) => {

   const config: PromiseListenerAsyncFunctionConfig<T,U> = {
      start: props.start,
      resolve: props.resolve,
      reject: props.reject,
      setPayload: props.setPayload,
      getPayload: props.getPayload,
      getError: props.getError
   };

   return props.listener.createAsyncFunction(config);
}

export const MakeAsyncFunction = <T,U=T>(props: MakeAsyncFunctionProps<T,U>) => {

   const [asyncFunction, setAsyncFunction ] = useState<PromiseListenerAsyncFunction | undefined>();
   const asyncFunctionRef = useRef<PromiseListenerAsyncFunction | undefined>();

   const {
      listener,
      start,
      resolve,
      reject,
      setPayload,
      getPayload,
      getError
   } = props;

   useEffect(()=>{
      setAsyncFunction((oldAsyncFunction) => {
         if (oldAsyncFunction) {
            oldAsyncFunction.unsubscribe();
         }
         const asyncFunction = createAsyncFunction({
            listener: listener,
            start: start,
            resolve: resolve,
            reject: reject,
            setPayload: setPayload,
            getPayload: getPayload,
            getError: getError
         } as MakeAsyncFunctionProps<T,U>);
         asyncFunctionRef.current = asyncFunction;
         return asyncFunction;
      });
      return () => {
         if (asyncFunctionRef.current) {
            asyncFunctionRef.current.unsubscribe();
            asyncFunctionRef.current = undefined;
         }
      }
   },[
      listener,
      start,
      resolve,
      reject,
      setPayload,
      getPayload,
      getError
   ]);

   return (asyncFunction) ? props.children(asyncFunction.asyncFunction) : null;
}
